import type { Posting } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import { SKIP_REASON_LABEL } from "@/lib/skipReasons";
import { lookup } from "@/lib/lookup";

/** Where the application stands, and what the person may do with it. */

export type ChipTone = "queued" | "applied" | "skipped" | "outline";

export interface StatusChip {
    label: string;
    tone: ChipTone;
}

/**
 * The chip a posting wears for the person: where their application to it
 * stands, or that there is none yet. A failed tailor is the person's to
 * send or skip, so it needs them, as the Queue says.
 */
export function applicationChip(
    application: Application | null | undefined,
    posting?: Pick<Posting, "closed_at"> | null,
): StatusChip {
    if (!application) {
        return posting?.closed_at
            ? { label: "Closed", tone: "skipped" }
            : { label: "Not yet added", tone: "skipped" };
    }
    switch (application.status) {
        case "MATCHED":
            return application.queued_by
                ? { label: "In the queue", tone: "queued" }
                : { label: "Matched", tone: "outline" };
        case "TAILORING":
            return { label: "Processing", tone: "queued" };
        case "NEEDS_YOU":
            return application.has_resume
                ? { label: "Ready to send", tone: "queued" }
                : { label: "Needs you", tone: "queued" };
        case "FAILED":
            return { label: "Needs you", tone: "queued" };
        case "APPLIED":
            return { label: "Applied", tone: "applied" };
        case "SKIPPED":
            return { label: "Skipped", tone: "skipped" };
        case "CLOSED":
            return { label: "Closed", tone: "skipped" };
    }
}

/** Whether the person can still record it sent: not while it is written, not once it is done with. */
export function canMarkSent(application: Application | null | undefined): boolean {
    return (
        application?.status === "MATCHED" ||
        application?.status === "NEEDS_YOU" ||
        application?.status === "FAILED"
    );
}

/** Whether it can still be skipped. A posting with no application can, by its id. */
export function canSkip(application: Application | null | undefined): boolean {
    if (!application) return true;
    return !["APPLIED", "SKIPPED", "CLOSED"].includes(application.status);
}

/**
 * Whether a posting being read offers Skip: only the envoy's stored match,
 * waiting with nobody having queued it. For you is a live search, so that
 * match is the one record of a recommendation. Once a tailor starts, the
 * record no longer says whose it was, so the Queue's own Skip is the way out.
 */
export function offersSkip(application: Application | null | undefined): boolean {
    return application?.status === "MATCHED" && !application.queued_by;
}

/** The reason a skip was recorded with, in the person's words: "location". */
export function skipReasonWords(reason: string | null | undefined): string {
    if (!reason) return "no reason given";
    const label = lookup(SKIP_REASON_LABEL as Record<string, string>, reason);
    return (label ?? reason).toLowerCase();
}
